import { KnzDiagnostic, KnzNode } from '../types';
import { ParsedBlock } from './parseBlock';

export type BuildTreeResult = {
  roots: KnzNode[];
  diagnostics: KnzDiagnostic[];
};

/**
 * 解析済みブロック群から描画ツリーを構築する（案C）。
 *
 * - どの `children` からも参照されていないブロックを「ルート」とし、定義順に並べる。
 * - `children` で参照された子は単独描画しない（ルートから除外）。
 * - 未定義 id 参照は warning（該当箇所のみ無視）。
 * - 循環参照は error（訪問済み集合で検出）。
 * - id を持たないブロックはルート扱い（children 参照対象にはならない）。
 */
export const buildTree = (blocks: ParsedBlock[]): BuildTreeResult => {
  const diagnostics: KnzDiagnostic[] = [];

  // id -> block のマップ（重複 id は警告し、先勝ち）
  const byId = new Map<string, ParsedBlock>();
  for (const b of blocks) {
    if (b.id) {
      if (byId.has(b.id)) {
        diagnostics.push({
          severity: 'warning',
          message: `id が重複しています: ${b.id}（先に定義されたブロックを使用します）`,
          line: b.startLine,
        });
        continue;
      }
      byId.set(b.id, b);
    }
  }

  // children から参照された id 集合
  const referenced = new Set<string>();
  for (const b of blocks) {
    for (const cid of b.childrenIds) referenced.add(cid);
  }

  // ノードへ再帰変換（循環検出用に訪問中集合を持つ）
  const toNode = (block: ParsedBlock, visiting: Set<string>): KnzNode => {
    const childNodes: KnzNode[] = [];
    for (const cid of block.childrenIds) {
      const child = byId.get(cid);
      if (!child) {
        diagnostics.push({
          severity: 'warning',
          message: `未定義の id を children で参照しています: ${cid}`,
          line: block.startLine,
        });
        continue;
      }
      if (child.id && visiting.has(child.id)) {
        diagnostics.push({
          severity: 'error',
          message: `循環参照を検出しました: ${child.id}`,
          line: block.startLine,
        });
        continue;
      }
      const nextVisiting = new Set(visiting);
      if (child.id) nextVisiting.add(child.id);
      childNodes.push(toNode(child, nextVisiting));
    }
    return {
      name: block.name,
      id: block.id,
      params: block.params,
      children: childNodes,
      line: block.startLine,
    };
  };

  // ルート = 参照されていないブロック（定義順を維持）
  const roots: KnzNode[] = [];
  const reachable = new Set<string>();

  const markReachable = (block: ParsedBlock) => {
    if (block.id) reachable.add(block.id);
    for (const cid of block.childrenIds) {
      const child = byId.get(cid);
      if (child && child.id && !reachable.has(child.id)) markReachable(child);
    }
  };

  for (const b of blocks) {
    const isReferenced = b.id ? referenced.has(b.id) : false;
    if (isReferenced) continue;
    const visiting = new Set<string>();
    if (b.id) visiting.add(b.id);
    roots.push(toNode(b, visiting));
    markReachable(b);
  }

  // ルートから到達不能なブロック群のうち、children で相互参照しているものは
  // 循環（ルートを持たない参照サイクル）とみなして error にする。
  for (const b of blocks) {
    if (!b.id) continue;
    if (reachable.has(b.id)) continue;
    if (b.childrenIds.length === 0) continue;
    diagnostics.push({
      severity: 'error',
      message: `循環参照を検出しました: ${b.id}`,
      line: b.startLine,
    });
  }

  return { roots, diagnostics };
};
